import React, { useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

export default function DieselReportPage() {
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0,7) + '-01');
  const [toDate, setToDate]     = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/operations/sessions/?page_size=500&ordering=started_date`);
      const sessions = res.data.results || [];

      // Filter by date range
      const filtered = sessions.filter(s => s.started_date >= fromDate && s.started_date <= toDate);

      // Initialize summary
      const summary = {
        compressor: { litres: 0, sessions: 0 },
        generator:  { litres: 0, sessions: 0 },
        tipper:     { litres: 0, sessions: 0 },
        poclain:    { litres: 0, sessions: 0 },
        other:      { litres: 0, sessions: 0 },
      };

      const dailyData = {};

      filtered.forEach(session => {
        try {
          const notes = JSON.parse(session.notes || '{}');
          const entries = notes.day_entries || [];

          entries.forEach(entry => {
            const wt = entry.work_type;
            const data = entry.data || {};
            const date = entry.entry_date || session.started_date;

            if (!dailyData[date]) dailyData[date] = {
              compressor: 0, generator: 0, tipper: 0, poclain: 0, other: 0
            };

            if (wt === 'DRILLING') {
              const machineData = data.machine_data || {};
              Object.values(machineData).forEach(machine => {
                const fuel = parseFloat(machine.compressor?.fuel || 0);
                if (fuel > 0) {
                  summary.compressor.litres += fuel;
                  summary.compressor.sessions += 1;
                  dailyData[date].compressor += fuel;
                }
              });
            }

            if (wt === 'WIRE_SAW') {
              const machineData = data.machine_data || {};
              Object.values(machineData).forEach(machine => {
                const diesel = parseFloat(machine.diesel_consumption || 0);
                if (diesel > 0) {
                  summary.generator.litres += diesel;
                  summary.generator.sessions += 1;
                  dailyData[date].generator += diesel;
                }
              });
            }

            if (wt === 'TRANSPORT') {
              const fuel = parseFloat(data.fuel_consumption || 0);
              const vehicle = data.vehicle_type || '';
              if (fuel > 0) {
                if (vehicle.toLowerCase().includes('tipper')) {
                  summary.tipper.litres += fuel;
                  summary.tipper.sessions += 1;
                  dailyData[date].tipper += fuel;
                } else if (vehicle.toLowerCase().includes('poclain')) {
                  summary.poclain.litres += fuel;
                  summary.poclain.sessions += 1;
                  dailyData[date].poclain += fuel;
                } else {
                  summary.other.litres += fuel;
                  summary.other.sessions += 1;
                  dailyData[date].other += fuel;
                }
              }
            }
          });
        } catch {}
      });

      const total = Object.values(summary).reduce((s, v) => s + v.litres, 0);
      // Fetch current diesel stock
      let dieselStock = 0;
      try {
        const stockRes = await api.get('/purchase/items/?search=Diesel');
        const dieselItem = (stockRes.data.results||[]).find(i=>i.name==='Diesel');
        dieselStock = parseFloat(dieselItem?.current_stock||0);
      } catch {}
      setReport({ summary, dailyData, total, sessionCount: filtered.length, dieselStock });
      
    } finally {
      setLoading(false);
    }
  };

  const inr = v => Number(v).toFixed(2);

  const MACHINES = [
    { key: 'compressor', label: 'Compressor (Drilling)',   color: 'var(--brand)' },
    { key: 'generator',  label: 'Generator (Wire Saw)',    color: 'var(--blue)' },
    { key: 'tipper',     label: 'Tipper (Transport)',      color: 'var(--amber)' },
    { key: 'poclain',    label: 'Poclain (Transport)',     color: 'var(--green)' },
    { key: 'other',      label: 'Other Vehicles',          color: 'var(--text-muted)' },
  ];

  return (
    <Layout title="Diesel Consumption Report" subtitle="Machine-wise fuel consumption analysis">
      {/* Filters */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>Report Filters</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'end' }}>
          <div className="form-group" style={{ flex:'1 1 160px' }}>
            <label className="form-label">From Date *</label>
            <input className="form-input" type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} max={new Date().toISOString().split('T')[0]}/>
          </div>
          <div className="form-group" style={{ flex:'1 1 160px' }}>
            <label className="form-label">To Date *</label>
            <input className="form-input" type="date" value={toDate} onChange={e=>setToDate(e.target.value)} max={new Date().toISOString().split('T')[0]}/>
          </div>
          <div className="form-group" style={{ flex:'0 0 auto' }}>
            <label className="form-label" style={{ opacity:0 }}>x</label>
            <button className="btn btn-primary" onClick={generate} disabled={loading}>
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {report && (
        <div>
          {/* Print Button */}
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
            <button className="btn btn-secondary btn-sm" onClick={()=>{
              sessionStorage.setItem('printDieselReport', JSON.stringify({
                summary: report.summary,
                dailyData: report.dailyData,
                total: report.total,
                sessionCount: report.sessionCount,
                dieselStock: report.dieselStock,
                filters: { from: fromDate, to: toDate }
              }));
              setTimeout(()=>{ window.open('/print/diesel-report', '_blank'); }, 100);
            }}>🖨 Print / Save as PDF</button>
          </div>
          {/* KPI Summary */}
          <div className="kpi-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:16 }}>
            <div className="kpi-card brand">
              <div className="kpi-value">{inr(report.total)} L</div>
              <div className="kpi-label">Total Diesel Used</div>
            </div>
            <div className="kpi-card blue">
              <div className="kpi-value">{report.sessionCount}</div>
              <div className="kpi-label">Sessions in Range</div>
            </div>
            <div className="kpi-card amber">
              <div className="kpi-value">{report.sessionCount > 0 ? inr(report.total / report.sessionCount) : '0.00'} L</div>
              <div className="kpi-label">Avg per Session</div>
            </div>
            <div className="kpi-card green">
              <div className="kpi-value">{report.dieselStock.toFixed(2)} L</div>
              <div className="kpi-label">Current Stock</div>
            </div>
          </div>

          {/* Machine-wise Breakdown */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>
              Machine-wise Breakdown
            </div>
            <div style={{ overflowX:'auto' }}>
              <table>
              <thead>
                <tr>
                  <th>Machine / Category</th>
                  <th>Sessions</th>
                  <th>Total Litres</th>
                  <th>% of Total</th>
                  <th>Usage Bar</th>
                </tr>
              </thead>
              <tbody>
                {MACHINES.filter(m => report.summary[m.key].litres > 0).map((m, i) => {
                  const val = report.summary[m.key];
                  const pct = report.total > 0 ? (val.litres / report.total * 100).toFixed(1) : 0;
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight:600, color:m.color }}>{m.label}</td>
                      <td style={{ fontFamily:'DM Mono,monospace' }}>{val.sessions}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:m.color }}>{inr(val.litres)} L</td>
                      <td style={{ fontFamily:'DM Mono,monospace' }}>{pct}%</td>
                      <td style={{ minWidth:120 }}>
                        <div style={{ background:'var(--border)', borderRadius:4, height:8, overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:m.color, borderRadius:4, transition:'width 0.5s' }}/>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* Total Row */}
                <tr style={{ borderTop:'2px solid var(--border)', background:'var(--bg-primary)' }}>
                  <td style={{ fontWeight:700 }}>Total</td>
                  <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700 }}>
                    {Object.values(report.summary).reduce((s,v)=>s+v.sessions,0)}
                  </td>
                  <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:'var(--brand)' }}>{inr(report.total)} L</td>
                  <td style={{ fontWeight:700 }}>100%</td>
                  <td/>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

          {/* Daily Breakdown */}
          {Object.keys(report.dailyData).length > 0 && (
            <div className="card">
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>
                Daily Breakdown
              </div>
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Compressor</th>
                      <th>Generator</th>
                      <th>Tipper</th>
                      <th>Poclain</th>
                      <th>Other</th>
                      <th>Daily Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.dailyData).sort().map(([date, data], i) => {
                      const dayTotal = Object.values(data).reduce((s,v)=>s+v,0);
                      return (
                        <tr key={i}>
                          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600 }}>{date}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--brand)' }}>{data.compressor > 0 ? `${inr(data.compressor)} L` : '—'}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--blue)' }}>{data.generator > 0 ? `${inr(data.generator)} L` : '—'}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--amber)' }}>{data.tipper > 0 ? `${inr(data.tipper)} L` : '—'}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--green)' }}>{data.poclain > 0 ? `${inr(data.poclain)} L` : '—'}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--text-muted)' }}>{data.other > 0 ? `${inr(data.other)} L` : '—'}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:'var(--brand)' }}>{inr(dayTotal)} L</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.total === 0 && (
            <div className="card" style={{ textAlign:'center', padding:'40px 20px', color:'var(--text-muted)' }}>
              No diesel consumption recorded for the selected date range.
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}